// ============================================================================
// /people/subcontractors — the Subcontractors hub (item #683; TCSD-0371, 0373).
// One nav entry, one header button (+ Add), four tabs under the title:
//   My subs · Pipeline · Compliance · More
// HR lands on Compliance; everyone else on My subs. The tab lives in the URL
// (?view=) so every tab is reachable from a link (TCSD-0082); Compliance keeps
// its own ?tab= for its sections, exactly as before.
// ============================================================================

import { isHrSeat } from '../../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import { TabBar, type TabItem } from '../../../shared/components';
import { useAuth } from '../../../shared/auth';
import SubcontractorsPage from '../../hr-onboarding/subcontractors-page';
import { useHubData, inScope } from './use-hub-data';
import { useHubScope } from './use-hub-scope';
import { MySubsTab } from './my-subs-tab';
import { PipelineTab } from './pipeline-tab';
import { AddSubSheet } from './add-sub-sheet';
import { ShareLinkSheet } from './share-link-sheet';

export type HubView = 'mysubs' | 'pipeline' | 'compliance' | 'more';

function parseView(raw: string | null, complianceTab: string | null, role: string | null): HubView {
  if (raw === 'mysubs' || raw === 'pipeline' || raw === 'compliance' || raw === 'more') return raw;
  // An older link names a Compliance section (?tab=insurance from the dashboard
  // widgets) — it still opens that section.
  if (complianceTab) return 'compliance';
  return isHrSeat(role) ? 'compliance' : 'mysubs';
}

export const MORE_LINKS: Array<{ label: string; to: string }> = [
  { label: 'Directory — search & compare', to: '/people/subcontractors/directory' },
  { label: 'Tiers', to: '/people/subcontractors?view=compliance&tab=tiers' },
  { label: 'Account managers', to: '/people/subcontractors?view=compliance&tab=managers' },
  { label: 'Discovery', to: '/people/subcontractors/discovery' },
  { label: 'Signal', to: '/people/subcontractors/signal' },
];

export default function SubcontractorsHub() {
  const { profile } = useAuth();
  const [params, setParams] = useSearchParams();
  const view = parseView(params.get('view'), params.get('tab'), profile?.role ?? null);
  const data = useHubData();
  const { scope, setScope } = useHubScope(data.myMarkets.length > 0);
  const [adding, setAdding] = useState(false);
  const [sharing, setSharing] = useState(false);
  // A link that names a scope (Home's "My subs" row counts MY markets) opens on
  // that scope, so the number tapped is the number shown; it becomes the
  // remembered choice, as if the toggle had been flipped.
  const scopeParam = params.get('scope');
  useEffect(() => {
    if ((scopeParam === 'mine' || scopeParam === 'all') && data.myMarkets.length > 0) {
      setScope(scopeParam);
      const next = new URLSearchParams(params);
      next.delete('scope');
      setParams(next, { replace: true });
    }
  }, [scopeParam, data.myMarkets.length, setScope, params, setParams]);
  const tileParam = params.get('tile');
  const initialTile = tileParam === 'bench' || tileParam === 'risk' || tileParam === 'nosub' ? tileParam : null;

  const leadCount = useMemo(
    () => data.subs.filter((s) => s.status === 'Lead' && inScope(s, scope, data.myMarkets, data.me)).length,
    [data.subs, scope, data.myMarkets, data.me],
  );

  const tabs: TabItem<HubView>[] = [
    { id: 'mysubs', label: 'My subs' },
    { id: 'pipeline', label: 'Pipeline', count: leadCount },
    { id: 'compliance', label: 'Compliance' },
    { id: 'more', label: 'More' },
  ];

  const go = (v: HubView) => {
    const next = new URLSearchParams();
    next.set('view', v);
    setParams(next, { replace: false });
  };

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight text-tcs-text">Subcontractors</h1>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-tcs-inverse px-3 py-2 text-sm font-semibold text-tcs-inverse-ink hover:bg-tcs-inverse-hover"
        >
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>
      <TabBar tabs={tabs} activeId={view} onChange={go} className="mb-4 overflow-x-auto" />

      {view === 'mysubs' && (
        <div className="mx-auto max-w-xl">
          <MySubsTab
            data={data}
            scope={scope}
            setScope={setScope}
            onShare={() => setSharing(true)}
            onLeads={() => go('pipeline')}
            initialTile={initialTile}
          />
        </div>
      )}
      {view === 'pipeline' && <PipelineTab subs={data.subs} loading={data.loading} error={data.error} refetch={data.refetch} />}
      {view === 'compliance' && <SubcontractorsPage embedded />}
      {view === 'more' && (
        <ul className="mx-auto max-w-xl divide-y divide-tcs-border rounded-lg border border-tcs-border">
          {MORE_LINKS.map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="flex items-center justify-between px-3 py-3 text-sm font-medium text-tcs-text">
                {l.label}
                <ChevronRight className="h-4 w-4 text-tcs-text-muted" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {adding && <AddSubSheet defaultMarkets={scope === 'mine' ? data.myMarkets : []} onClose={() => setAdding(false)} />}
      {sharing && <ShareLinkSheet onClose={() => setSharing(false)} />}
    </div>
  );
}
